import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { UserPlus, Loader2, CheckCircle, AlertTriangle, ArrowRight, Building2 } from 'lucide-react';
import PageHeader from '../../components/PageHeader';
import Button from '../../components/ui/Button';
import billingService, { describeTerm } from '../../services/billing';
import subscriptionService from '../../services/subscription';
import type { SubscriptionPlan } from '../../types/subscription';
import type { OnboardResult } from '../../types/billing';
import { BillingGate, ErrorBanner, Field } from '../../components/billing/billingUi';
import { errorText, inputClass, useBillingAccess } from '../../components/billing/billingHelpers';
import TrialPlanPicker from '../../components/billing/TrialPlanPicker';
import { draftsToTrials, type TrialDraft } from '../../components/billing/trialDrafts';
import TermFields from '../../components/billing/TermFields';
import { emptyTermDraft, termDraftProblem, termDraftToInput, type TermDraft } from '../../components/billing/termDrafts';

const STATES = ['VIC', 'NSW', 'QLD', 'SA', 'WA', 'TAS', 'NT', 'ACT'];
const STATE_TZ: Record<string, string> = {
  VIC: 'Australia/Melbourne', NSW: 'Australia/Sydney', ACT: 'Australia/Sydney', TAS: 'Australia/Hobart',
  QLD: 'Australia/Brisbane', SA: 'Australia/Adelaide', NT: 'Australia/Darwin', WA: 'Australia/Perth',
};

/**
 * /billing/onboard — sign a customer up in one screen: a sales desk, or a
 * stand at a trade show with the grower in front of you.
 *
 * Only the business name and the admin's email are required. Everything else
 * is optional, and trials are a tap per module. The server creates the org,
 * trials and offer together, then sends the invite; the rep is not added to
 * the customer's org.
 *
 * The success screen leads with "Onboard another", because at a show the next
 * person is already waiting.
 */
export default function BillingOnboard() {
  const navigate = useNavigate();
  const { canBill } = useBillingAccess();

  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [name, setName] = useState('');
  const [adminEmail, setAdminEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [abn, setAbn] = useState('');
  const [state, setState] = useState('VIC');
  const [billingEmail, setBillingEmail] = useState('');
  const [note, setNote] = useState('');
  const [trials, setTrials] = useState<Record<number, TrialDraft>>({});
  const [withOffer, setWithOffer] = useState(false);
  const [offer, setOffer] = useState<TermDraft>(emptyTermDraft());

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<OnboardResult | null>(null);

  useEffect(() => {
    subscriptionService.getAvailablePlans().then(r => { if (r.success) setPlans(r.data); }).catch(() => {});
  }, []);

  const reset = () => {
    setName(''); setAdminEmail(''); setPhone(''); setAbn(''); setBillingEmail(''); setNote('');
    setTrials({}); setWithOffer(false); setOffer(emptyTermDraft());
    setResult(null); setError(null);
    window.scrollTo?.(0, 0);
  };

  const trialProblem = Object.entries(trials).find(([, d]) => !Object.values(d.seats).some(n => n > 0));
  const problem =
    !name.trim() ? 'Enter the business name' :
    !/^\S+@\S+\.\S+$/.test(adminEmail.trim()) ? "Enter the admin's email" :
    trialProblem ? `Give the ${plans.find(p => p.id === Number(trialProblem[0]))?.name ?? 'trial'} at least one seat` :
    withOffer ? termDraftProblem(offer) : null;

  const submit = async () => {
    if (problem) { setError(problem); return; }
    setSubmitting(true);
    setError(null);
    try {
      const res = await billingService.onboard({
        organisation: {
          name: name.trim(),
          contact_email: adminEmail.trim(),
          contact_phone: phone.trim() || undefined,
          billing_email: billingEmail.trim() || undefined,
          abn: abn.trim() || undefined,
          state,
          timezone: STATE_TZ[state],
          note: note.trim() || undefined,
        },
        admin: { email: adminEmail.trim() },
        trials: draftsToTrials(trials, note.trim() || undefined),
        terms: withOffer ? [termDraftToInput(offer)] : [],
      });
      setResult(res);
    } catch (err) {
      setError(errorText(err, 'Onboarding failed'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <PageHeader title="Onboard a Customer" subtitle="New organisation, admin invite and trials in one go" icon={<UserPlus className="h-6 w-6 text-white" />} />
      <BillingGate canBill={canBill}>
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8">
          {result ? (
            <OnboardDone result={result} trials={trials} plans={plans} offer={withOffer ? offer : null}
              onAnother={reset} onOpen={() => navigate(`/billing/organisations/${result.organisation.id}`)} />
          ) : (
            <div className="space-y-6">
              <ErrorBanner message={error} />

              <section className="bg-white rounded-xl border border-neutral-200 p-5 space-y-4">
                <h2 className="font-semibold text-neutral-900">The customer</h2>
                <Field label="Business name" htmlFor="ob-name">
                  <input id="ob-name" value={name} onChange={e => setName(e.target.value)} className={inputClass} autoFocus placeholder="e.g. Silvan Valley Orchards Pty Ltd" />
                </Field>
                <Field label="Admin's email" htmlFor="ob-admin" hint="They get an invite and become this organisation's admin. An existing account is added straight away.">
                  <input id="ob-admin" type="email" inputMode="email" autoComplete="off" value={adminEmail} onChange={e => setAdminEmail(e.target.value)} className={inputClass} />
                </Field>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <Field label="Phone" htmlFor="ob-phone">
                    <input id="ob-phone" type="tel" value={phone} onChange={e => setPhone(e.target.value)} className={inputClass} />
                  </Field>
                  <Field label="ABN" htmlFor="ob-abn">
                    <input id="ob-abn" inputMode="numeric" value={abn} onChange={e => setAbn(e.target.value)} className={inputClass} />
                  </Field>
                  <Field label="State" htmlFor="ob-state" hint="Sets the organisation's timezone">
                    <select id="ob-state" value={state} onChange={e => setState(e.target.value)} className={inputClass}>
                      {STATES.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </Field>
                </div>
                <Field label="Invoices go to (optional)" htmlFor="ob-billing" hint="Blank = the admin's email">
                  <input id="ob-billing" type="email" value={billingEmail} onChange={e => setBillingEmail(e.target.value)} className={inputClass} />
                </Field>
              </section>

              <section className="bg-white rounded-xl border border-neutral-200 p-5 space-y-3">
                <div>
                  <h2 className="font-semibold text-neutral-900">Trials</h2>
                  <p className="text-sm text-neutral-500">Free until the trial ends, then invoiced to continue. They're reminded a week before.</p>
                </div>
                <TrialPlanPicker plans={plans} drafts={trials} onChange={setTrials} />
              </section>

              <section className="bg-white rounded-xl border border-neutral-200 p-5 space-y-3">
                <label className="flex items-center gap-2 font-semibold text-neutral-900">
                  <input type="checkbox" checked={withOffer} onChange={e => setWithOffer(e.target.checked)} />
                  Add a discount or free period
                </label>
                {withOffer && <TermFields draft={offer} onChange={setOffer} plans={plans} idPrefix="ob-term" />}
              </section>

              <section className="bg-white rounded-xl border border-neutral-200 p-5">
                <Field label="Note (optional)" htmlFor="ob-note" hint="Where you met them, what they grow — kept on the account history">
                  <input id="ob-note" value={note} onChange={e => setNote(e.target.value)} className={inputClass} />
                </Field>
              </section>

              <div className="flex flex-wrap gap-3 justify-end">
                <Button variant="outline" onClick={() => navigate('/billing')} disabled={submitting}>Cancel</Button>
                <Button variant="primary" onClick={submit} disabled={submitting}>
                  {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <UserPlus className="h-4 w-4" />}
                  Create &amp; send invite
                </Button>
              </div>
            </div>
          )}
        </div>
      </BillingGate>
    </div>
  );
}

function OnboardDone({ result, trials, plans, offer, onAnother, onOpen }: {
  result: OnboardResult;
  trials: Record<number, TrialDraft>;
  plans: SubscriptionPlan[];
  offer: TermDraft | null;
  onAnother: () => void;
  onOpen: () => void;
}) {
  const { invite } = result;
  return (
    <div className="bg-white rounded-xl border border-neutral-200 p-6 space-y-5">
      <div className="flex items-start gap-3">
        <CheckCircle className="h-8 w-8 text-success-600 flex-shrink-0" />
        <div className="min-w-0">
          <h2 className="text-xl font-bold text-neutral-900 break-words">{result.organisation.name} is set up</h2>
          {invite.status === 'invited' && (
            <p className="text-neutral-600 break-words">Invite sent to <strong>{invite.email}</strong>. It's valid for 7 days.</p>
          )}
          {invite.status === 'added_existing_user' && (
            <p className="text-neutral-600 break-words">
              <strong>{invite.email}</strong> already had an account and is now this organisation's admin. They'll see it next time they sign in.
            </p>
          )}
        </div>
      </div>

      {invite.status === 'failed' && (
        <div className="bg-warning-50 border border-warning-200 rounded-lg p-3 text-sm text-warning-800 flex items-start gap-2">
          <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" />
          <span className="min-w-0 break-words">
            The organisation was created but the invite didn't go out ({invite.error}). Resend it from the organisation's page.
          </span>
        </div>
      )}

      {(Object.keys(trials).length > 0 || offer) && (
        <ul className="text-sm text-neutral-700 space-y-1">
          {Object.entries(trials).map(([planId, d]) => (
            <li key={planId}>• {plans.find(p => p.id === Number(planId))?.name}: {d.days}-day trial</li>
          ))}
          {offer && <li>• {describeTerm({ kind: offer.kind, value: offer.value, plan_name: plans.find(p => p.id === Number(offer.plan_id))?.name, ends_on: offer.ends_on || null })}</li>}
        </ul>
      )}

      <div className="flex flex-wrap gap-3">
        <Button variant="primary" onClick={onAnother}>
          <UserPlus className="h-4 w-4" />
          Onboard another
        </Button>
        <Button variant="outline" onClick={onOpen}>
          <Building2 className="h-4 w-4" />
          Open organisation
          <ArrowRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}
